import { useCallback, useInsertionEffect, useRef } from "react"

/**
 * Returns a function with a stable identity that always calls the latest
 * callback (the useEvent pattern from the React docs). Pass the result to
 * memoized children: they skip re-renders when unrelated state changes, while
 * the handler still reads fresh props/state when invoked.
 *
 * The wrapped function must not be called during render.
 */
export const useStableCallback = <Args extends unknown[], Result>(
  callback: (...args: Args) => Result
): ((...args: Args) => Result) => {
  const callbackRef = useRef(callback)
  useInsertionEffect(() => {
    callbackRef.current = callback
  })
  return useCallback((...args: Args) => callbackRef.current(...args), [])
}
